import { AUTH_URL } from '@bn-games/auth-client';

/**
 * The one HTTP path for every history endpoint: URL building, cookie
 * credentials, status → error mapping and body decoding live here so the
 * three public functions in `history.ts` are each a single call.
 *
 * Unlike `auth-client`'s `fetchMe`, failures are NOT collapsed into `null`.
 * Failure modes are meaningful to the user — signed out (401), spamming
 * resigns (429), a bad payload (400) — so they are returned as data rather
 * than thrown: a signed-out visitor is an expected state, not an exception.
 */

export type GameHistoryErrorKind =
  | 'unauthenticated'
  | 'bad_request'
  | 'forbidden'
  | 'not_found'
  | 'conflict'
  | 'expired'
  | 'rate_limited'
  | 'network'
  | 'unknown';

export interface GameHistoryError {
  kind: GameHistoryErrorKind;
  /** HTTP status, or `0` when the request never reached the server. */
  status: number;
  /** The server's `{ error }` message when it sent one, else a fallback. */
  message: string;
  /** Present on `rate_limited` when the response carried `Retry-After`. */
  retryAfterSeconds?: number;
}

export type GameHistoryResult<T> =
  | { ok: true; value: T }
  | { ok: false; error: GameHistoryError };

/** Narrows an unknown decoded body without committing to `any`. */
export type Decoder<T> = (body: unknown) => T | null;

const NETWORK_STATUS = 0;
const STATUS_UNAUTHORIZED = 401;
const STATUS_FORBIDDEN = 403;
const STATUS_NOT_FOUND = 404;
const STATUS_CONFLICT = 409;
const STATUS_GONE = 410;
const STATUS_TOO_MANY_REQUESTS = 429;
const STATUS_BAD_REQUEST = 400;
const STATUS_OK_MIN = 200;
const STATUS_OK_MAX = 299;
const DECIMAL_RADIX = 10;

// Same status vocabulary as `@bn-games/social-client` — this package hits
// the same Worker, even though not every status is reachable from these
// three routes today (e.g. no route here returns 409/410 yet). Kept anyway
// for forward-compatibility and consistency, not pruned.
const KIND_BY_STATUS: ReadonlyMap<number, GameHistoryErrorKind> = new Map<
  number,
  GameHistoryErrorKind
>([
  [STATUS_BAD_REQUEST, 'bad_request'],
  [STATUS_UNAUTHORIZED, 'unauthenticated'],
  [STATUS_FORBIDDEN, 'forbidden'],
  [STATUS_NOT_FOUND, 'not_found'],
  [STATUS_CONFLICT, 'conflict'],
  [STATUS_GONE, 'expired'],
  [STATUS_TOO_MANY_REQUESTS, 'rate_limited'],
]);

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

export function ok<T>(value: T): GameHistoryResult<T> {
  return { ok: true, value };
}

export function fail<T>(error: GameHistoryError): GameHistoryResult<T> {
  return { ok: false, error };
}

export function historyUrl(path: string, params?: Record<string, string>): string {
  const base = AUTH_URL + path;
  if (params === undefined) return base;
  const query = new URLSearchParams(params).toString();
  return query === '' ? base : base + '?' + query;
}

/**
 * The 401 body is plain text, and a proxy may substitute HTML for any status,
 * so an unreadable error body degrades to the fallback rather than failing.
 */
async function errorMessage(response: Response, fallback: string): Promise<string> {
  try {
    const body: unknown = await response.json();
    if (isRecord(body) && typeof body['error'] === 'string') {
      return body['error'];
    }
  } catch {
    // fall through
  }
  return fallback;
}

function retryAfterSeconds(response: Response): number | undefined {
  const raw = response.headers.get('Retry-After');
  if (raw === null) return undefined;
  const seconds = Number.parseInt(raw, DECIMAL_RADIX);
  return Number.isFinite(seconds) ? seconds : undefined;
}

async function toError(response: Response): Promise<GameHistoryError> {
  const kind = KIND_BY_STATUS.get(response.status) ?? 'unknown';
  const message = await errorMessage(response, 'request failed (' + response.status + ')');
  const error: GameHistoryError = { kind, status: response.status, message };
  if (kind !== 'rate_limited') return error;

  const seconds = retryAfterSeconds(response);
  return seconds === undefined ? error : { ...error, retryAfterSeconds: seconds };
}

export async function request<T>(
  url: string,
  init: RequestInit,
  decode: Decoder<T>
): Promise<GameHistoryResult<T>> {
  let response: Response;
  try {
    response = await fetch(url, { ...init, credentials: 'include' });
  } catch {
    return fail({ kind: 'network', status: NETWORK_STATUS, message: 'network error' });
  }

  if (response.status < STATUS_OK_MIN || response.status > STATUS_OK_MAX) {
    return fail(await toError(response));
  }

  let body: unknown;
  try {
    body = await response.json();
  } catch {
    return fail({ kind: 'unknown', status: response.status, message: 'malformed response' });
  }

  const value = decode(body);
  if (value === null) {
    return fail({ kind: 'unknown', status: response.status, message: 'unexpected response' });
  }
  return ok(value);
}

export function jsonRequest<T>(
  url: string,
  method: string,
  body: unknown,
  decode: Decoder<T>
): Promise<GameHistoryResult<T>> {
  return request(
    url,
    {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
    decode
  );
}
